import React from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { FastField } from "formik";
import { useQuery } from "@apollo/client/react";
import Input from "src/components/form/Input";
import useAuth from "src/hooks/useAuth";
import useAvailableProjectTypesOptions from "src/views/settings/hooks/useAvailableProjectTypesOptions";
import { PROJECT_INTEGRATION_RECORDS_QUERY } from "src/constants/graphql/queries";

export default function KeyDetails({ project }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const movewareEnabled = user.company.enabledIntegrationProviders?.includes("moveware");
  const { data: integrationData } = useQuery(PROJECT_INTEGRATION_RECORDS_QUERY, {
    variables: { id: project?.id },
    skip: !movewareEnabled || !project?.id,
  });
  const movewareLinked = Boolean(
    integrationData?.project?.externalRecords?.find(
      externalRecord => externalRecord.recordType === "storage_account"
    )
  );
  const warehouseDropdownEnabled = settings.warehouse_id_dropdown_menu;
  const warehouses = project?.warehouses || [];
  const projectTypeOptions = useAvailableProjectTypesOptions();

  return (
    <Paper>
      <Box p={2}>
        <Typography fontWeight={500}>Key Details</Typography>
        <Grid container spacing={1} sx={{ pt: 1.5 }}>
          {warehouseDropdownEnabled ? (
            <Grid size={12}>
              <Autocomplete
                multiple
                open={false}
                size="small"
                options={warehouses}
                value={warehouses}
                getOptionLabel={warehouse => warehouse.name}
                isOptionEqualToValue={(option, value) => option.warehouseId === value.warehouseId}
                popupIcon={null}
                clearIcon={null}
                ChipProps={{
                  onDelete: undefined,
                  deleteIcon: null,
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={`Warehouses Inventory In`}
                    variant="outlined"
                    inputProps={{
                      ...params.inputProps,
                      readOnly: true,
                    }}
                  />
                )}
              />
            </Grid>
          ) : (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Input
                type="autoExpand"
                disabled
                name="clientName"
                label={`${settings.filter_field1} Name`}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Input type="autoExpand" name="name" label={`${settings.filter_field2} Name`} showNAButton={false} />
          </Grid>
          {settings.receiving_wizard && (
            <>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FastField name="projectType">
                  {({ field, form }) => (
                    <FormControl size="small" variant="outlined" fullWidth>
                      <InputLabel shrink>{settings.filter_field2} Field Template</InputLabel>
                      <Select
                        {...field}
                        label={`${settings.filter_field2} Field Template`}
                        value={field.value || ""}
                        onChange={e => form.setFieldValue("projectType", e.target.value)}
                        notched
                        displayEmpty
                      >
                        {projectTypeOptions.map(([value, label]) => (
                          <MenuItem key={value} value={value}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </FastField>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Input
                  type="autoExpand"
                  name="orderNumber"
                  label="Order Number"
                  showNAButton={false}
                  disabled={movewareLinked}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Input
                  type="autoExpand"
                  name="customerPoNumber"
                  label={`${settings.filter_field2} PO Number`}
                  showNAButton={false}
                  disabled={movewareLinked}
                />
              </Grid>
            </>
          )}
          <Grid size={12}>
            <Input
              type="text"
              name="customerNotes"
              label={`${settings.filter_field2} Notes`}
              showNAButton={false}
              disabled={movewareLinked}
            />
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}
